import qs from 'qs'
import invariant from 'tiny-invariant'

import type { definitions, paths } from '@luxuryescapes/contract-svc-accommodation'

import request from 'api/requestUtils'
import { split } from 'lib/array/arrayUtils'
import { ghaQueryParams, type GhaPriceMatchParams } from 'lib/metasearch/ghaPriceMatch'
import wait from 'lib/promise/wait'
import { mergeObjects } from 'lib/object/objectUtils'
import generateOccupancyStringByRoom from 'lib/offer/generateOccupancyStringByRoom'

import mapProperty from './mappers/propertyMapper'
import mapPropertyAnytimeRate from './mappers/propertyAnytimeRateMapper'
import mapPropertyCalendarPrices from './mappers/propertyCalendarPricesMapper'
import mapPropertyRates, { getPropertyChannelMarkupLeadOfferIds } from './mappers/propertyRatesMapper'
import type { ChannelMarkupSource } from 'lib/channelMarkup/channelMarkupV2'

/* ************ */
/* Get property */
/* ************ */

interface PropertyParams {
  regionCode: string
  currency: string
  previewMode: boolean
}

// NB: We key off `paths` rather than `actions` to ensure we're tightly coupled with the endpoint's
// actual type.
type ServerGetPropertyContent = paths['/api/accommodation/content/properties/{propertyId}']['get']
// NB: `brand` parameter is supplied by `brandLoader` middleware.
type ServerGetPropertyContentRequestQueryParams = Omit<ServerGetPropertyContent['parameters']['query'], 'brand'>
type ServerGetPropertyContentResponse = ServerGetPropertyContent['responses']['200']['content']['application/json']

export async function getProperty(
  propertyId: string,
  params: PropertyParams,
): Promise<App.Property.Property | undefined> {
  const queryParams: ServerGetPropertyContentRequestQueryParams = {
    region: params.regionCode,
    currency: params.currency,
    allowMysteryRooms: true,
    allFacilities: true,
    preview: params.previewMode,
  }
  const query = qs.stringify(queryParams)

  const response = await request.get<ServerGetPropertyContentResponse>(
    `/api/accommodation/content/properties/${propertyId}?${query}`,
  )
  const serverPropertyContent = response.result
  if (serverPropertyContent) {
    return mapProperty(serverPropertyContent, params.previewMode)
  }
}

/* ************************* */
/* Get property anytime rate */
/* ************************* */

interface PropertyAnytimeRateParams {
  regionCode: string
  currency: string
}

// NB: We key off `paths` rather than `actions` to ensure we're tightly coupled with the endpoint's
// actual type.
type ServerAnytimePricing = paths['/api/accommodation/properties/anytime-pricing']['get']
// NB: `brand` parameter is supplied by `brandLoader` middleware.
type ServerAnytimePricingRequestQueryParams = Omit<ServerAnytimePricing['parameters']['query'], 'brand'>
type ServerAnytimePricingResponse = ServerAnytimePricing['responses']['200']['content']['application/json']

type ServerAnytimePricingForProperties = ServerAnytimePricingResponse['result']['properties']

/**
 * Search renders one tile per property, so fetching per caller would fan out a request for every
 * LPC/LPP tile on the page. The endpoint accepts `propertyIds`, so callers landing within a couple
 * of frames of each other share a single request.
 */
const ANYTIME_PRICING_BATCH_WINDOW_MS = 32
/** Keeps the accumulated query string well clear of URL length limits. */
const ANYTIME_PRICING_BATCH_SIZE = 50

interface AnytimePricingBatch {
  propertyIds: Set<string>
  promise: Promise<ServerAnytimePricingForProperties> | undefined
}

// Region and currency are whole-request parameters, so calls that disagree on either must not
// share a batch.
const anytimePricingBatches = new Map<string, AnytimePricingBatch>()

async function requestAnytimePricing(
  propertyIds: Array<string>,
  params: PropertyAnytimeRateParams,
): Promise<ServerAnytimePricingForProperties> {
  const responses = await Promise.all(
    split(propertyIds, ANYTIME_PRICING_BATCH_SIZE).map(batchedPropertyIds => {
      const queryParams: ServerAnytimePricingRequestQueryParams = {
        region: params.regionCode,
        currency: params.currency,
        propertyIds: batchedPropertyIds,
      }
      return request.get<ServerAnytimePricingResponse>(
        `/api/accommodation/properties/anytime-pricing?${qs.stringify(queryParams)}`,
      )
    }),
  )

  return mergeObjects(responses.map(response => response.result.properties))
}

async function getAccumulatedAnytimePricing(
  propertyId: string,
  params: PropertyAnytimeRateParams,
): Promise<ServerAnytimePricingForProperties> {
  // The accumulator is module state, so on the server it would be shared across concurrent
  // renders — and request preprocessors (locale, for one) vary per render. SSR only ever asks for
  // one property at a time anyway, so there's nothing to batch there.
  if (typeof window === 'undefined') return requestAnytimePricing([propertyId], params)

  const batchKey = `${params.regionCode}|${params.currency}`
  const batch = anytimePricingBatches.get(batchKey) ?? { propertyIds: new Set<string>(), promise: undefined }
  anytimePricingBatches.set(batchKey, batch)
  batch.propertyIds.add(propertyId)

  if (!batch.promise) {
    batch.promise = wait(ANYTIME_PRICING_BATCH_WINDOW_MS).then(async() => {
      // Release the batch before awaiting the request so later callers accumulate a fresh one
      // rather than attaching to a batch whose ids are already in flight.
      anytimePricingBatches.delete(batchKey)

      return requestAnytimePricing(Array.from(batch.propertyIds), params)
    })
  }

  return batch.promise
}

export async function getPropertyAnytimeRate(
  propertyId: string,
  params: PropertyAnytimeRateParams,
): Promise<{
  anytimeRate: App.Property.AnytimeRate
  anytimePromotionRate: App.Property.AnytimePromotionRate | undefined
  anytimeRoomRates: { [roomId: string]: App.Property.AnytimeRoomRate } | undefined
} | undefined> {
  const serverAnytimePricingForProperties = await getAccumulatedAnytimePricing(propertyId, params)
  const serverAnytimePricingForProperty = serverAnytimePricingForProperties[propertyId]
  if (serverAnytimePricingForProperty) {
    return mapPropertyAnytimeRate(serverAnytimePricingForProperty, params.currency)
  }
}

/* **************************** */
/* Get property calendar prices */
/* **************************** */

interface PropertyCalendarPricesParams {
  /**
   * Inclusive start of month to fetch, in ISO 8601 (`YYYY-MM`)
   *
   * The resultant date range with `endIsoYearMonth` should not span more than a couple of years.
   */
  startIsoYearMonth: string
  /**
   * Inclusive end of month to fetch, in ISO 8601 (`YYYY-MM`)
   *
   * The resultant date range with `startIsoYearMonth` should not span more than a couple of years.
   */
  endIsoYearMonth: string
  /** Length of stay, in nights */
  duration: number
  /** Room occupancies */
  occupancies: Array<App.Occupants>
  /** Requested currency code, in ISO 4217 (e.g. `"AUD"`) */
  currency: string
}

// NB: We key off `paths` rather than `actions` to ensure we're tightly coupled with the endpoint's
// actual type.
type ServerCalendarPrices = paths['/api/accommodation/properties/{propertyId}/calendar-prices']['get']
// NB: There's no `brand` parameter.
type ServerCalendarPricesRequestQueryParams = ServerCalendarPrices['parameters']['query']
type ServerCalendarPricesResponse = ServerCalendarPrices['responses']['200']['content']['application/json']

export async function getPropertyCalendarPrices(
  propertyId: string,
  params: PropertyCalendarPricesParams,
): Promise<{ [isoDate: string]: App.Property.CalendarDay }> {
  invariant(params.startIsoYearMonth <= params.endIsoYearMonth)
  const allIsoYearMonths = []
  // NB: Constructed dates are for the first of the month at midnight UTC.
  const month = new Date(params.startIsoYearMonth)
  const endMonth = new Date(params.endIsoYearMonth)
  // oxlint-disable-next-line no-unmodified-loop-condition
  while (month <= endMonth) {
    // NB: Because we exclusively manipulate UTC dates, we needn't worry about the local time zone
    // and DST transitions.
    allIsoYearMonths.push(month.toISOString().substring(0, 7))
    month.setUTCMonth(month.getUTCMonth() + 1)
  }
  // Accommodation service permits up to 12 months per request.
  const isoYearMonthsChunks = split(allIsoYearMonths, 12)
  const queryChunks: Array<string> = isoYearMonthsChunks.map(isoYearMonths => {
    const queryParams: ServerCalendarPricesRequestQueryParams = {
      los: String(params.duration),
      months: isoYearMonths.join(','),
      currency: params.currency,
      occupancies: params.occupancies.map(generateOccupancyStringByRoom),
    }
    return qs.stringify(queryParams)
  })

  // Two things to keep in mind follow.
  //
  //  1. To keep things simple, if one request fails, we don't bother retrying or salvaging the
  //     other requests.
  //  2. It's on the caller to specify a "reasonable" `startIsoYearMonth` and `endIsoYearMonth`
  //     range that won't lead to an excessive number of requests.
  const mappedChunks = await Promise.all(queryChunks.map(async query => {
    const response = await request.get<ServerCalendarPricesResponse>(
      `/api/accommodation/properties/${propertyId}/calendar-prices?${query}`,
      { excludeBrand: true },
    )
    const serverCalendarPricesResponseResult = response.result
    if (serverCalendarPricesResponseResult) {
      return mapPropertyCalendarPrices(serverCalendarPricesResponseResult)
    }
    return {}
  }))
  return mergeObjects(mappedChunks)
}

/* ****************** */
/* Get property rates */
/* ****************** */

interface PropertyRatesParams {
  checkIn: string
  checkOut: string
  occupancies: Array<App.Occupants>
  regionCode: string
  currency: string
  previewMode: boolean
  source: string
  dynamicAvailability: boolean
  enablePropertyCollect: boolean
  includeBundles: boolean
  utmContent: string | undefined
  roomTypeId: string | undefined
  roomRateId: string | undefined
  roomRateGroupId: string | undefined
  areFlightsEnabled: boolean
  markupSource?: ChannelMarkupSource
  /**
   * Resolves the lead pricing for the stay's nights of the given LPC and bedbank offers, read by a
   * v2 price band. Called with the offers behind the response's rates, before they are mapped.
   */
  resolveChannelMarkupLeadPricing?: (offerIds: Array<string>) => Promise<Record<string, App.ChannelMarkupLeadPricing>>
  gha?: GhaPriceMatchParams
}

// NB: We key off `paths` rather than `actions` to ensure we're tightly coupled with the endpoint's
// actual type.
type ServerUnifiedAvailability = paths['/api/accommodation/properties/availability']['get']
// NB: `brand` parameter is supplied by `brandLoader` middleware.
type ServerUnifiedAvailabilityRequestQueryParams = Omit<ServerUnifiedAvailability['parameters']['query'], 'brand'>
type ServerUnifiedAvailabilityResponse = ServerUnifiedAvailability['responses']['200']['content']['application/json']
type ServerValidateBundleRate = paths['/api/accommodation/properties/availability/bundles/validate']['post']
type ServerValidateBundleRateRequestBody = Omit<definitions['ValidateBundleRateBody'], 'brand'>
type ServerValidateBundleRateResponse = ServerValidateBundleRate['responses']['200']['content']['application/json']

const GHA_ROOM_RATE_ID_PATTERN = /^(.+?)(?:_([A-Z]{3}))?$/

function parseGhaRoomRateId(roomRateId: string | undefined): [string | undefined, string | undefined] {
  if (!roomRateId) return [undefined, undefined]
  const result = GHA_ROOM_RATE_ID_PATTERN.exec(roomRateId)
  if (!result) return [undefined, undefined]
  return [result[1], result[2]]
}

function mapHighlightedReason(
  utmContent: string | undefined,
): ServerUnifiedAvailabilityRequestQueryParams['highlightedReason'] {
  switch (utmContent) {
    case 'gha-lpc':
    case 'gha-lpp':
      return utmContent
  }
}

export async function getPropertyRates(
  propertyId: string,
  params: PropertyRatesParams,
): Promise<Record<string, Array<App.RawPropertyRoomRates>>> {
  const [highlightedRateId, highlightedRateCurrency] = parseGhaRoomRateId(params.roomRateId)
  const queryParams: ServerUnifiedAvailabilityRequestQueryParams = {
    checkIn: params.checkIn,
    checkOut: params.checkOut,
    occupancies: params.occupancies.map(generateOccupancyStringByRoom),
    region: params.regionCode,
    currency: params.currency,
    preview: params.previewMode,
    source: params.source,
    dynamicAvailability: params.dynamicAvailability,
    upliftRecommendedRate: true,
    enablePropertyCollect: params.enablePropertyCollect,
    allowMysteryRooms: true,
    includeBundles: params.includeBundles,
    propertyIds: [propertyId],
    highlightedReason: highlightedRateId ? mapHighlightedReason(params.utmContent) : undefined,
    highlightedRoomId: params.roomTypeId,
    highlightedRateId,
    highlightedRateGroupId: params.roomRateGroupId,
    highlightedRateCurrency,
    includeUnavailableResults: true,
    ...ghaQueryParams(params.gha),
  }
  const query = qs.stringify(queryParams)

  const response = await request.get<ServerUnifiedAvailabilityResponse>(
    `/api/accommodation/properties/availability?${query}`,
    { credentials: 'include' },
  )
  const serverAvailabilityResponseProperty = response.result.properties[propertyId]
  if (serverAvailabilityResponseProperty) {
    const leadOfferIds = getPropertyChannelMarkupLeadOfferIds(serverAvailabilityResponseProperty)
    const channelMarkupLeadPricingByOfferId = leadOfferIds.length && params.resolveChannelMarkupLeadPricing
      ? await params.resolveChannelMarkupLeadPricing(leadOfferIds)
      : {}
    return mapPropertyRates(serverAvailabilityResponseProperty, response.result.accommodationSessionId, params.currency, params.areFlightsEnabled, params.markupSource, params.occupancies.length, channelMarkupLeadPricingByOfferId)
  }
  return {}
}

/* ***************************** */
/* Validate property rate bundle */
/* ***************************** */

interface ValidatePropertyRateBundleParams {
  checkIn: string
  checkOut: string
  occupancies: Array<App.Occupants>
  regionCode: string
  currency: string
  source: string
  dynamicAvailability: boolean
  propertyId: string
  rateId: string
  /** Mandatory bundle tickets; required alongside `bundleId` */
  tickets?: Array<{ experienceId: string; productId: string; ticketPrice: number }>
  /** Mandatory bundle identifier expected on the rate */
  bundleId?: string
  /**
   * Opt-in bundles the customer has selected. The backend rejects (400) a request carrying both these
   * and a mandatory `bundleId`/`tickets` pair, so callers must issue separate requests.
   */
  optionalBundles?: Array<{
    bundleId: string
    tickets: Array<{ experienceId: string; productId: string; ticketPrice?: number }>
  }>
}

export async function validatePropertyRateBundle(params: ValidatePropertyRateBundleParams): Promise<boolean> {
  const body: ServerValidateBundleRateRequestBody = {
    checkIn: params.checkIn,
    checkOut: params.checkOut,
    occupancies: params.occupancies.map(generateOccupancyStringByRoom),
    region: params.regionCode,
    currency: params.currency,
    source: params.source,
    dynamicAvailability: params.dynamicAvailability,
    propertyId: params.propertyId,
    rateId: params.rateId,
    tickets: params.tickets,
    bundleId: params.bundleId,
    ...(params.optionalBundles?.length && { optionalBundles: params.optionalBundles }),
  }

  const response = await request.post<ServerValidateBundleRateResponse, ServerValidateBundleRateRequestBody>(
    '/api/accommodation/properties/availability/bundles/validate',
    body,
    { credentials: 'include' },
  )

  return !!response?.result
}

/* ****************** */
/* Property mappings  */
/* ****************** */

type ServerGetPropertyMappings = paths['/api/accommodation/properties/mappings']['get']
type ServerGetPropertyMappingsResponse =
  ServerGetPropertyMappings['responses']['200']['content']['application/json']

export interface AccommodationPropertyMappings {
  /** Sellable LE offers built on this property. Unordered. */
  offerIds: Array<string>
  /** Sellable svc-bedbank property ids, which double as bedbank offer ids. Unordered. */
  bedbankIds: Array<string>
}

const inFlightMappings = new Map<string, Promise<AccommodationPropertyMappings>>()

/**
 * The sellable LE offer and bedbank ids an accommodation property maps to.
 *
 * svc-accommodation keys properties by its own UUID; every customer-facing offer URL is keyed by
 * an svc-offer or svc-bedbank id. This public endpoint is the only bridge between the two, and it
 * returns only approved, scheduled mappings, so an id it hands back has somewhere live to link to.
 *
 * Responses carry a five minute `Cache-Control`; concurrent lookups of the same property are
 * coalesced here so a page of rows sharing a property issues one request rather than one each.
 */
export async function getAccommodationPropertyMappings(
  propertyId: string,
): Promise<AccommodationPropertyMappings> {
  const inFlight = inFlightMappings.get(propertyId)
  if (inFlight) return inFlight

  const pending = request
    .get<ServerGetPropertyMappingsResponse>(
      `/api/accommodation/properties/mappings?propertyId=${encodeURIComponent(propertyId)}`,
    )
    .then(response => ({
      offerIds: response.result.offerIds ?? [],
      bedbankIds: response.result.bedbankIds ?? [],
    }))
    .finally(() => { inFlightMappings.delete(propertyId) })

  inFlightMappings.set(propertyId, pending)

  return pending
}
